import { useEffect, useState, type ReactNode } from 'react';
import {
  ArrowLeft,
  ArrowUpRight,
  BellRing,
  Check,
  Clock,
  Globe,
  LineChart,
  Lock,
  ShieldCheck,
  Sparkles,
  Star,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import appLogoUrl from './assets/ascensa-icon.png';
import appStoreBadgeUrl from './assets/app-store-badge.svg';
import { legalBySlug, legalDocs, type LegalDoc } from './legal';
import { Markdown } from './Markdown';

type LinkItem = { label: string; href: string };

const policyLinks: LinkItem[] = legalDocs.map((doc) => ({
  label: doc.label,
  href: `/policies/${doc.slug}`,
}));

const marquee = [
  'NASDAQ + NYSE',
  'REFRESHED DAILY',
  'REVENUE + EPS HISTORY',
  'HISTORICAL PRICE',
  'NO ADS EVER',
  'INDEPENDENT RANKING',
  'PAPER TRADING',
];

const shots = [
  { src: '/screenshots/top-performers.png', alt: 'Ascensa Top Performers screen', caption: 'Top Performers' },
  { src: '/screenshots/ticker-detail.png', alt: 'Ascensa NVDA ticker detail screen', caption: 'Ticker Detail' },
  { src: '/screenshots/financials.png', alt: 'Ascensa NVDA financials screen', caption: 'Financials' },
  { src: '/screenshots/watchlist.png', alt: 'Ascensa watchlist screen', caption: 'Watchlist' },
  { src: '/screenshots/paper-trading.png', alt: 'Ascensa paper trading portfolio screen', caption: 'Paper Trading' },
];

const supportEmail = 'support@ascensa.xyz';
const privacyEmail = 'privacy@ascensa.xyz';
const legalEmail = 'legal@ascensa.xyz';
const planPrice = '$4.99';
const companyName = 'Ascensa Technologies LLC';

type Plan = {
  name: string;
  tone: 'free' | 'plus';
  ribbon?: string;
  price: string;
  period: string;
  features: string[];
  cta: string;
  fine: string;
  featured?: boolean;
};

const plans: Plan[] = [
  {
    name: 'Free',
    tone: 'free',
    price: '$0',
    period: ' / month',
    features: [
      'Top Performers from the Dow 30 and Nasdaq 100',
      'Price charts from this year out to ten years',
      'Revenue, earnings and company profile',
      'Latest headlines for every stock',
      'Alerts and earnings-date reminders',
      'No ads and no sponsored stocks',
    ],
    cta: 'Coming soon',
    fine: 'No card required. Free forever.',
  },
  {
    name: 'Plus',
    tone: 'plus',
    ribbon: 'Most popular',
    price: planPrice,
    period: ' / month',
    features: [
      'Everything in Free, plus the S&P 500 and all stocks *',
      'Unlimited watchlist',
      'Paper trading portfolios with virtual cash',
    ],
    cta: 'Coming soon',
    fine: 'Cancel anytime, and still keep the free plan.',
    featured: true,
  },
];

export function App() {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  const doc = pathname.startsWith('/policies/')
    ? legalBySlug.get(pathname.slice('/policies/'.length))
    : undefined;

  useEffect(() => {
    if (doc) document.title = `${doc.label} | Ascensa`;
    else if (pathname.startsWith('/policies')) document.title = 'Legal | Ascensa';
    else document.title = 'Ascensa | A daily shortlist of US stocks';
  }, [pathname, doc]);

  if (doc) return <LegalDocPage doc={doc} />;
  if (pathname.startsWith('/policies')) return <PoliciesPage />;
  return <MarketingSite />;
}

function Wordmark({ size = 'md' }: { size?: 'md' | 'sm' }) {
  return (
    <a className={`mark mark-${size}`} href="/" aria-label="Ascensa home">
      <img src={appLogoUrl} alt="" />
      <span>Ascensa</span>
    </a>
  );
}

function MarketingSite() {
  return (
    <>
      <TopBar />
      <div className="shell">
        <section className="hero">
        <div className="hero-glow" aria-hidden="true" />
        <p className="tagline">US stock screener · updated every trading day</p>
        <h1>
          The market, <span className="highlight">distilled.</span>
        </h1>
        <p className="hero-sub">
          Ascensa screens thousands of NASDAQ and NYSE tickers through a momentum algorithm, then hands you the
          historical charts, fundamentals and headlines to make an informed decision. No ads, no paid sponsored
          companies, no infinite feed.
        </p>
        <div className="launch-block" id="get-app">
          <p className="launch-flag">
            <Clock size={14} aria-hidden="true" /> Coming soon
          </p>
          <div className="store-row store-row-pending">
            <span className="store-badge" aria-label="Ascensa on the App Store, coming soon">
              <img src={appStoreBadgeUrl} alt="Download on the App Store — coming soon" />
            </span>
          </div>
        </div>
        <p className="hero-fine">
          Free for the Dow 30 and Nasdaq 100 · <Price value={planPrice} />/month adds the S&amp;P 500 and all stocks · no
          ads on either plan
        </p>
        <ShotDeck />
      </section>

      <Marquee />

      <section className="section" id="features">
        <SectionHead
          eyebrow="What's included"
          title="From ranked list to informed decision."
          sub="Serious research that fits in your pocket — read it on the commute or over coffee, and find strong performers that have not made the headlines yet."
        />
        <div className="tiles">
          <Tile icon={<TrendingUp size={22} />} title="Top Performers *">
            A daily list of stocks with proven price performance from profitable companies, independently
            ranked by our proprietary algorithm. One, two and three-year returns side by side, so trends are
            easy to spot.
          </Tile>
          <Tile icon={<LineChart size={22} />} title="Company profile">
            Every stock has price charts, revenue and earnings history, key statistics, a business summary and
            recent news.
          </Tile>
          <Tile icon={<Star size={22} />} title="Watchlist †">
            Save any stock you want to follow straight from the Top Performers list to create your own
            personalized list.
          </Tile>
          <Tile icon={<Wallet size={22} />} title="Paper trading †">
            Create multiple portfolios and test your ideas without risking real money. Buy and sell with
            virtual cash, then track your gains and losses against real market prices.
          </Tile>
          <Tile icon={<BellRing size={22} />} title="Alerts & reminders">
            Get notified when a new stock joins the Top Performers list, and add a company&rsquo;s next
            earnings date to your calendar in one tap.
          </Tile>
        </div>
        <p className="tiles-note">
          <span className="clause">
            * Free plan covers the Dow 30 and Nasdaq 100. Ascensa Plus adds the S&amp;P 500 and every NASDAQ and
            NYSE stock.
          </span>
          <span className="clause">† Available with Ascensa Plus.</span>
        </p>
      </section>

      <section className="section" id="pricing">
        <SectionHead
          eyebrow="Pricing"
          title={
            <>
              <span className="clause">Start with the market&rsquo;s leaders.</span>
              <span className="clause">Go all the way with Plus.</span>
            </>
          }
          sub="Free tracks the Dow 30 and Nasdaq 100. Plus opens up the S&P 500 and every NASDAQ and NYSE stock. No ads on either plan."
        />
        <div className="plans">
          {plans.map((plan) => (
            <article className={plan.featured ? 'plan plan-featured' : 'plan'} key={plan.name}>
              {plan.ribbon ? (
                <span className={`plan-ribbon plan-ribbon-${plan.tone}`}>{plan.ribbon}</span>
              ) : null}
              <p className="plan-family">
                <img src={appLogoUrl} alt="" /> Ascensa
              </p>
              <h3 className={`plan-name plan-name-${plan.tone}`}>{plan.name}</h3>
              <p className="plan-price">
                <Price value={plan.price} />
                <span className="plan-period">{plan.period}</span>
              </p>
              <hr />
              <ul>
                {plan.features.map((feature) => (
                  <li key={feature}>
                    <Check size={15} aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
              <span className={`plan-cta plan-cta-${plan.tone}`} aria-disabled="true">
                {plan.cta}
              </span>
              <p className="plan-fine">{plan.fine}</p>
            </article>
          ))}
        </div>
        <p className="plans-note">
          <span className="clause">
            * All stocks means every stock listed on NASDAQ and NYSE, the major U.S. exchanges.
          </span>
          <span className="clause">
            Ascensa does not cover other exchanges, OTC stocks, or non-U.S. listings.
          </span>
        </p>
      </section>

      <section className="section" id="standards">
        <SectionHead
          eyebrow="Our principles"
          title={
            <>
              <span className="clause">Ascensa is a stock research app,</span>
              <span className="clause">not a broker.</span>
            </>
          }
          sub="No advice, no ads, no data sales — and no exceptions."
        />
        <div className="pledges">
          <div><ShieldCheck size={20} aria-hidden="true" /><h3>We never tell you what to buy</h3><p>Ascensa organizes public market data. It does not recommend trades, place orders, or manage anyone&rsquo;s money.</p></div>
          <div><Globe size={20} aria-hidden="true" /><h3>US stocks, refreshed daily</h3><p>We screen top performers of NASDAQ and NYSE listings using each day&rsquo;s closing prices. Built for long-term investors.</p></div>
          <div><Sparkles size={20} aria-hidden="true" /><h3>No ads ever</h3><p>Subscriptions keep us running independently, so no company can pay to appear on the list. The free plan lets you see what Ascensa offers without committing to a subscription.</p></div>
          <div><Lock size={20} aria-hidden="true" /><h3>Your data, your privacy</h3><p>Your email, watchlist and portfolios stay in your account. We never sell your personal information to anyone.</p></div>
        </div>
      </section>

      <SiteFooter />
      </div>
    </>
  );
}

function TopBar({ minimal = false }: { minimal?: boolean }) {
  return (
    <header className={minimal ? 'topbar topbar-minimal' : 'topbar'} aria-label="Primary">
      <Wordmark />
      {minimal ? null : (
        <>
          <nav className="topbar-nav">
            <a href="#features">What's included</a>
            <a href="#pricing">Pricing</a>
            <a href="#standards">Our principles</a>
          </nav>
          <a className="btn btn-ghost" href="#get-app">
            Coming soon <ArrowUpRight size={15} aria-hidden="true" />
          </a>
        </>
      )}
    </header>
  );
}

// Renders the cents smaller so "$4.99" reads as a four-dollar price at a glance.
function Price({ value }: { value: string }) {
  const [dollars, cents] = value.split('.');
  if (!cents) return <>{value}</>;
  return (
    <span className="price">
      {dollars}
      <span className="price-cents">.{cents}</span>
    </span>
  );
}

function SectionHead({ eyebrow, title, sub }: { eyebrow: string; title: ReactNode; sub?: string }) {
  return (
    <header className="section-head">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {sub ? <p className="section-sub">{sub}</p> : null}
    </header>
  );
}

function Tile({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <article className="tile">
      <span className="tile-icon" aria-hidden="true">{icon}</span>
      <h3>{title}</h3>
      <p>{children}</p>
    </article>
  );
}

function ShotDeck() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % shots.length), 4600);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="deck" aria-label="Ascensa app screenshots">
      <div className="deck-rail">
        {shots.map((shot, i) => {
          const offset = (i - index + shots.length) % shots.length;
          // Show the active phone centred, the next one on the right and the previous one on the left.
          const position =
            offset === 0 ? '0' : offset === 1 ? '1' : offset === shots.length - 1 ? '2' : 'hidden';
          const hidden = position === 'hidden';
          return (
            <button
              type="button"
              key={shot.src}
              className={`deck-phone deck-pos-${position}`}
              aria-label={`Show ${shot.caption}`}
              aria-hidden={hidden || undefined}
              tabIndex={hidden ? -1 : undefined}
              onClick={() => setIndex(i)}
            >
              <img src={shot.src} alt={shot.alt} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Marquee() {
  // Six copies so one set can scroll fully off without exposing a gap on wide screens.
  const loop = Array.from({ length: 6 }, () => marquee).flat();

  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee-track">
        {loop.map((item, i) => (
          <span key={`${item}-${i}`}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function PoliciesPage() {
  return (
    <>
      <TopBar minimal />
      <div className="shell">
      <section className="hero hero-policy">
        <p className="tagline">Legal</p>
        <h1>Policy center</h1>
        <p className="hero-sub">
          Everything covering how Ascensa handles your account, your data and your subscription. These documents
          apply to the Ascensa app and website, operated by {companyName}.
        </p>
        <p className="hero-fine">
          Legal <a href={`mailto:${legalEmail}`}>{legalEmail}</a> · Privacy{' '}
          <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a> · Support{' '}
          <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
        </p>
      </section>
      <section className="section policy-grid" aria-label="Legal documents">
        {legalDocs.map((doc) => (
          <a className="policy-card" href={`/policies/${doc.slug}`} key={doc.slug}>
            <span>
              <strong>{doc.label}</strong>
              <small>{doc.summary}</small>
            </span>
            <ArrowUpRight size={17} aria-hidden="true" />
          </a>
        ))}
      </section>
      <SiteFooter />
      </div>
    </>
  );
}

function LegalDocPage({ doc }: { doc: LegalDoc }) {
  return (
    <>
      <TopBar minimal />
      <div className="shell">
        <nav className="doc-breadcrumb" aria-label="Breadcrumb">
          <a href="/policies">
            <ArrowLeft size={15} aria-hidden="true" /> All legal documents
          </a>
        </nav>
        <article className="doc-layout">
          <aside className="doc-side">
            <h2>Legal documents</h2>
            {legalDocs.map((entry) => (
              <a
                className={entry.slug === doc.slug ? 'doc-side-link doc-side-active' : 'doc-side-link'}
                href={`/policies/${entry.slug}`}
                key={entry.slug}
                aria-current={entry.slug === doc.slug ? 'page' : undefined}
              >
                {entry.label}
              </a>
            ))}
          </aside>
          <div className="doc-body">
            <header className="doc-head">
              <p className="tagline">Legal</p>
              <h1>{doc.label}</h1>
              <p className="doc-summary">{doc.summary}</p>
            </header>
            <Markdown source={doc.source} />
          </div>
        </article>
        <SiteFooter />
      </div>
    </>
  );
}

function SiteFooter() {
  return (
    <footer className="footer">
      <div className="footer-grid">
        <div className="footer-brand">
          <Wordmark size="sm" />
          <p>A daily shortlist of US stocks for people who love to do their own research.</p>
          <div className="footer-launch">
            <p className="launch-flag">
              <Clock size={14} aria-hidden="true" /> Coming soon
            </p>
            <div className="store-row store-row-pending">
              <span className="store-badge store-badge-sm" aria-label="Ascensa on the App Store, coming soon">
                <img src={appStoreBadgeUrl} alt="Download on the App Store — coming soon" />
              </span>
            </div>
          </div>
        </div>
        <nav aria-label="Legal">
          <h2>Legal</h2>
          {policyLinks.map((l) => <a href={l.href} key={l.href}>{l.label}</a>)}
        </nav>
        <nav aria-label="Support">
          <h2>Support</h2>
          <a href={`mailto:${supportEmail}`}>Contact us</a>
        </nav>
      </div>
      <div className="footer-bottom">
        <p>© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
        <p>
          Ascensa is a research tool and does not provide investment, tax, legal or accounting advice. Market
          data may be delayed or incomplete. Past performance does not guarantee future results.
        </p>
      </div>
    </footer>
  );
}
